import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MapPin } from "lucide-react";
import { Reveal, ContactLinks } from "../components/shared";
import { SiteLayout, Caps, Leaf } from "../components/site";
import { TEAM } from "../team";
import { HOURS, FORMATS, CONTACT, CATEGORIES, SCRIPT } from "../content";

const H2 = ({ children, className = "" }) => <h2 className={`font-newsreader font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#3F4A3C] ${className}`}>{children}</h2>;
const btn = "group inline-flex items-center gap-3 rounded-full px-7 py-4 font-jakarta text-sm transition-colors duration-500";

const Hero = () => (
  <section id="top" className="relative" data-testid="hero">
    <h1 className="sr-only" data-testid="hero-heading">Inner You Development</h1>
    <picture>
      <source media="(max-width: 1023px)" srcSet="/images/home-banner-square.jpg" />
      <img src="/images/home-banner.jpg" alt="Inner You Development tree of life with watercolour leaves and rolling hills" data-testid="hero-banner" className="block w-full h-auto" />
    </picture>
    <div className="relative max-w-[1360px] mx-auto px-6 sm:px-10 py-12 sm:py-16 text-center">
      <p className="font-script text-2xl sm:text-3xl text-[#5F7C8A]" data-testid="hero-script">{SCRIPT}</p>
      <p className="mt-4 max-w-xl mx-auto font-jakarta text-base sm:text-lg leading-relaxed text-[#4A4F45]" data-testid="hero-phrase">Psychology, social work, counselling and allied health for children, couples and families.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/book" data-testid="hero-book-btn" className={`${btn} bg-[#3F4A3C] text-[#F6F2E9] hover:bg-[#56654F]`}>Book a session <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></Link>
          <Link to="/enquire" data-testid="hero-enquiry-btn" className={`${btn} text-[#3F4A3C] border border-[#3F4A3C]/25 hover:bg-[#DCE6EC] hover:border-transparent`}>Send an enquiry</Link>
      </div>
    </div>
  </section>
);

const SERVICE_LINKS = [...CATEGORIES.map((c) => ({ label: c.title, to: `/services#${c.id}` })), { label: "Parenting programs", to: "/parenting" }, { label: "Family reports", to: "/family-reports" }];

const ServiceList = () => (
  <section className="relative overflow-hidden py-24 sm:py-32 bg-[#F9F6EF]" data-testid="services-section">
    <Leaf src="low" className="left-0 bottom-0 w-44 sm:w-80 lg:w-[30rem] opacity-85" />
    <div className="relative max-w-[1360px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-5"><Caps>What we offer</Caps><H2 className="mt-6">Mental health & allied health, <em className="text-[#5F7C8A]">under one roof.</em></H2></Reveal>
      <ul className="lg:col-span-7 border-t border-[#3F4A3C]/15">
        {SERVICE_LINKS.map((s) => (
          <li key={s.to}><Link to={s.to} data-testid={`home-service-${s.to.split(/[#/]/).pop()}`} className="group flex items-center justify-between gap-6 py-5 sm:py-6 border-b border-[#3F4A3C]/15 font-newsreader text-2xl sm:text-3xl text-[#3F4A3C] hover:text-[#5F7C8A] transition-colors">
            {s.label}<ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-500 group-hover:translate-x-1" /></Link></li>
        ))}
      </ul>
    </div>
  </section>
);

const TeamTeaser = () => (
  <section className="relative overflow-hidden py-24 sm:py-32" data-testid="team-teaser">
    <div className="relative max-w-[1360px] mx-auto px-5 sm:px-10">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div><Caps>Our team</Caps><H2 className="mt-5">The people <em className="text-[#5F7C8A]">behind the care.</em></H2></div>
        <Link to="/team" data-testid="home-meet-team" className="font-jakarta text-sm text-[#3F4A3C] underline underline-offset-4">Meet the whole team</Link>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-8">
        {TEAM.map((m, i) => (
          <Reveal key={m.id} delay={(i % 6) * 0.06}>
            <Link to={`/team#${m.id}`} data-testid={`teaser-${m.id}`} className="group block">
              <div className="aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[20px] bg-[#E3E9E4]"><img src={`/images/team/${m.id}.jpg`} alt={m.name} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" /></div>
              <p className="mt-3 font-newsreader text-lg text-[#3F4A3C]">{m.name.split(" ")[0]}</p>
              <p className="font-jakarta text-xs text-[#5F7C8A]">{m.role}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Visit = () => (
  <section className="px-4 sm:px-8 pb-20" data-testid="visit-section">
    <div className="max-w-[1360px] mx-auto overflow-hidden rounded-[40px] bg-[#DCE6EC] px-6 sm:px-12 lg:px-16 pt-16 sm:pt-20 grid lg:grid-cols-2 gap-x-14 gap-y-10">
      <Reveal><Caps>Visit & hours</Caps>
        <p className="mt-8 flex gap-3 font-jakarta text-[#4A4F45]"><MapPin className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} />{CONTACT.address}</p>
        <ContactLinks prefix="visit" className="mt-6 flex flex-col gap-4 font-jakarta text-[#3F4A3C]" linkClass="underline-offset-4 hover:underline" />
        <div className="mt-8 flex flex-wrap gap-2">{FORMATS.map((f) => <span key={f} className="font-jakarta text-xs rounded-full bg-[#F6F2E9] px-4 py-2 text-[#3F4A3C]">{f}</span>)}</div>
      </Reveal>
      <Reveal delay={0.15}>
        <ul className="border-t border-[#3F4A3C]/20" data-testid="hours-list">
          {HOURS.map((h) => <li key={h.day} className="flex justify-between gap-4 py-5 border-b border-[#3F4A3C]/20 font-jakarta"><span className="text-[#4A4F45]">{h.day}</span><span className="text-[#3F4A3C] font-medium text-right">{h.time}</span></li>)}
        </ul>
        <Link to="/enquire" data-testid="visit-enquiry-btn" className={`${btn} mt-10 bg-[#3F4A3C] text-[#F6F2E9] hover:bg-[#56654F]`}>Send an enquiry <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" /></Link>
      </Reveal>
      <div className="relative lg:col-span-2 -mx-6 sm:-mx-12 lg:-mx-16 mt-4 h-72 sm:h-96 lg:h-[30rem]" data-testid="visit-practice-photo-band">
        <img src="/images/practice-room.jpg" alt="A calm therapy room at Inner You Development with a green sofa, plants and soft lighting" loading="lazy" data-testid="visit-practice-photo" className="absolute inset-0 w-full h-full object-cover object-[center_60%]" />
        <div aria-hidden className="absolute inset-x-0 top-0 h-28 sm:h-40 bg-gradient-to-b from-[#E6EEDF] via-[#E6EEDF]/60 to-transparent" />
      </div>
    </div>
  </section>
);

export default function Sanctuary() {
  return (
    <SiteLayout testid="sanctuary-page" ack>
      <Hero />
      <ServiceList />
      <TeamTeaser />
      <Visit />
    </SiteLayout>
  );
}
